import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';
import { getPostBySlug } from '@/lib/blog';
import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} — RadiologyLive.ai`;
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', post.description);
    setMeta('keywords', post.keywords);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <article className="max-w-2xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" />
            Back to blog
          </Link>

          <header className="mb-10">
            <time className="text-sm text-muted-foreground">
              {new Date(post.date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              {' · '}{post.readingTime} min read
            </time>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mt-2">
              {post.title}
            </h1>
            {post.subtitle && (
              <p className="text-lg text-muted-foreground mt-3">{post.subtitle}</p>
            )}
            <p className="text-sm text-muted-foreground mt-3">{post.author}</p>
          </header>

          <div className="prose prose-neutral dark:prose-invert max-w-none prose-headings:tracking-tight prose-headings:text-white prose-p:text-gray-300 prose-li:text-gray-300 prose-strong:text-white prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-blockquote:text-gray-300 prose-em:text-gray-300">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
